import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { User, Lock, Eye, EyeOff, Wifi, ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import ktcLogo from '../assets/ktc.png';
import { resolveBackendBaseUrl } from '../utils/runtimeConfig';

const API = `${resolveBackendBaseUrl()}/api`;

const CustomerLogin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    identifier: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await axios.post(`${API}/portal/login`, formData);
      
      // Save token and customer data
      localStorage.setItem('ktc_customer_token', res.data.access_token);
      localStorage.setItem('ktc_customer', JSON.stringify(res.data.customer));
      
      toast.success(`Selamat datang, ${res.data.customer.name}!`);
      navigate('/portal');
    } catch (error) {
      const message = error.response?.data?.detail || 'Login gagal';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
      {/* Background Effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-20 w-96 h-96 bg-brand-blue/20 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-brand-red/20 rounded-full blur-[120px]" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md relative z-10"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <img 
              src={ktcLogo} 
              alt="KTC Logo" 
              className="h-16 mx-auto mb-4"
            />
          </Link>
          <h1 className="font-outfit text-2xl font-bold text-white">Portal Pelanggan</h1>
          <p className="text-gray-400 mt-2">Masuk untuk melihat tagihan & profil Anda</p>
        </div>

        {/* Login Form */}
        <div className="bg-[#121212] rounded-2xl p-8 border border-white/10">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-gray-400 text-sm mb-2">
                Nomor Internet
              </label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="text"
                  value={formData.identifier}
                  onChange={(e) => setFormData(prev => ({ ...prev, identifier: e.target.value }))}
                  className="w-full pl-12 pr-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-xl text-white focus:border-brand-blue focus:outline-none"
                  placeholder="Contoh: 000123"
                  required
                  data-testid="portal-identifier-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-gray-400 text-sm mb-2">
                Password Pelanggan
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  className="w-full pl-12 pr-12 py-3 bg-[#0A0A0A] border border-white/10 rounded-xl text-white focus:border-brand-blue focus:outline-none"
                  placeholder="********"
                  required
                  data-testid="portal-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-gray-500 text-xs mt-2">
                Gunakan password pelanggan yang terdaftar di sistem.
              </p>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-brand-blue hover:bg-brand-blue/90 py-3"
              data-testid="portal-login-button"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Wifi className="w-5 h-5 mr-2" />
                  Masuk
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-white/10 text-center">
            <p className="text-gray-500 text-sm">
              Belum terdaftar? Hubungi admin untuk mendaftar layanan internet KTC.
            </p>
          </div>
        </div>

        {/* Back to Home */}
        <div className="text-center mt-6">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Kembali ke Beranda
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default CustomerLogin;



